import * as React from 'react'
import { useRouter } from 'next/navigation'
import { User } from 'lucide-react'

import { useLocale } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { useTranslation } from '~/app/i18n/client'
import { MapleNavigationDropdown } from '../components/navigation-dropdown'

type ProfileOption = {
  key: string
  value: string
  path?: string
  disabled?: boolean
}

type ProfileMenuProps = {
  color?: string
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ color }) => {
  const router = useRouter()
  const { tenant } = useApi()
  const { t } = useTranslation()
  const { locale } = useLocale()

  const profileOptions: ProfileOption[] = [
    {
      key:
        tenant?.theme === 'pointsAtWork' && tenant?.flags?.loginPage
          ? '/login'
          : 'api/auth/login',
      value: t('nav.login'),
    },
    ...(tenant?.signupUrl
      ? [
          {
            key: tenant.signupUrl,
            value: t('nav.signUp'),
          },
        ]
      : []),
  ]

  const onOptionSelect = async (url: string) => {
    // Handle signup URL based on whether it's internal or external
    if (url === tenant?.signupUrl) {
      if (url.startsWith('/')) {
        // Internal URL - open in same window
        window.location.href = url
      } else {
        // External URL - open in new tab
        window.open(url, '_blank')
      }
      return
    }

    if (url === 'api/auth/login') {
      if (tenant?.loginLink?.[locale]) {
        window.location.href = tenant.loginLink[locale]
        return
      } else {
        await fetch(
          `/api/auth/login?returnUrl=${encodeURIComponent(
            window.location.href,
          )}&locale=${locale}`,
        )
          .then((response) => response.json())
          .then((response) => {
            window.location.href = response.authRedirect.url
          })
        return
      }
    }

    router.push(url || '/')
  }

  return (
    <MapleNavigationDropdown
      id="profile-menu"
      placeholder={<User color={`${color ? color : 'currentColor'}`} />}
      options={profileOptions}
      onOptionSelect={onOptionSelect}
    />
  )
}
